import { useMutation } from '@apollo/client';
import type { UpdateDocumentInternalAuditResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateDocumentInternalAuditResultDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { UpdateDocumentInternalAuditResultInput } from './useUpdateDocumentInternalAuditResultTRPC';
import { useUpdateDocumentInternalAuditResultTRPC } from './useUpdateDocumentInternalAuditResultTRPC';

export const useUpdateDocumentInternalAuditResult = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [updateGraphQL, graphqlState] = useMutation(
    UpdateDocumentInternalAuditResultDocument,
    {
      update: (cache) => {
        evictField(cache, 'document_internal_audit_result');
        evictField(cache, 'internal_audit_report');
        evictField(cache, 'document_internal_audit_result_aggregate');
      },
    }
  );

  const trpcMutation = useUpdateDocumentInternalAuditResultTRPC();

  const updateDocumentInternalAuditResult = async (
    variables: UpdateDocumentInternalAuditResultInput
  ): Promise<UpdateDocumentInternalAuditResultMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateDocumentInternalAuditResult(variables);
    }

    const result = await updateGraphQL({ variables });
    if (!result.data) {
      throw new Error('Failed to update document internal audit result');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      updateDocumentInternalAuditResult,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateDocumentInternalAuditResult,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
